/**
 * Sesli gorusmenin tarayici tarafi: mikrofon seviyesi, kayit, oynatma.
 *
 * ## `createCallAudio` TIKLAMA ANINDA ve SENKRON cagrilmali
 *
 * iOS Safari `AudioContext`i ve `<audio>` oynatmayi ancak bir kullanici
 * etkilesiminin ICINDE baslatmaya izin veriyor. `getUserMedia` bir `await`
 * ve ondan sonra etkilesim "tuketilmis" sayiliyor: baglam askida kalir,
 * yanitlar hic calmazdi. Bu yuzden baglam ve ses ogesi tiklamada kuruluyor,
 * mikrofon sonra aciliyor.
 *
 * ## Yanki giderme ACIK
 *
 * Kullanici yanitin ustune konusabiliyor (soz kesme). Hoparlorden gelen
 * yanit sesi mikrofona kacarsa kullanicinin sozu sanilirdi; tarayicinin
 * yanki gidermesi bunun buyuk kismini temizliyor, kalan icin algilayicinin
 * soz kesme esigi daha yuksek (bkz. `VoiceActivity`).
 */

import type { MicHandle, PlayerHandle, RecorderHandle } from './callSession'
import { unlockElement } from './speech'

type AudioContextCtor = typeof AudioContext

export interface CallAudio {
  openMic(): Promise<MicHandle>
  player: PlayerHandle
  close(): void
}

export function createCallAudio(): CallAudio {
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  const context = Ctor ? new Ctor() : null
  void context?.resume().catch(() => undefined)

  const element = new Audio()
  unlockElement(element)

  let finishPlayback: (() => void) | null = null
  let currentUrl: string | null = null
  const releaseUrl = () => {
    if (currentUrl) URL.revokeObjectURL(currentUrl)
    currentUrl = null
  }

  const player: PlayerHandle = {
    play(audio) {
      player.stop()
      return new Promise<void>((resolve) => {
        const done = () => {
          if (finishPlayback !== done) return
          finishPlayback = null
          releaseUrl()
          resolve()
        }
        finishPlayback = done
        currentUrl = URL.createObjectURL(audio)
        element.src = currentUrl
        element.onended = done
        element.onerror = done
        element.play().catch(done)
      })
    },
    stop() {
      element.pause()
      finishPlayback?.()
    },
  }

  async function openMic(): Promise<MicHandle> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    let analyser: AnalyserNode | null = null
    let samples: Float32Array<ArrayBuffer> | null = null
    if (context) {
      analyser = context.createAnalyser()
      analyser.fftSize = 1024
      context.createMediaStreamSource(stream).connect(analyser)
      samples = new Float32Array(analyser.fftSize)
    }
    return {
      level() {
        if (!analyser || !samples) return 0
        analyser.getFloatTimeDomainData(samples)
        let sum = 0
        for (const value of samples) sum += value * value
        return Math.sqrt(sum / samples.length)
      },
      record: () => recordStream(stream),
      close() {
        for (const track of stream.getTracks()) track.stop()
      },
    }
  }

  return {
    openMic,
    player,
    close() {
      player.stop()
      element.removeAttribute('src')
      void context?.close().catch(() => undefined)
    },
  }
}

function recordStream(stream: MediaStream): RecorderHandle {
  // Bicimi TARAYICI seciyor (Chrome webm/opus, Safari mp4/aac) - sabit bir
  // bicim dayatmak Safari'de kaydi kiriyordu (bkz. `startRecording`).
  const recorder = new MediaRecorder(stream)
  const chunks: Blob[] = []
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data)
  }
  recorder.start()
  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType }))
        if (recorder.state === 'inactive') resolve(new Blob(chunks, { type: recorder.mimeType }))
        else recorder.stop()
      }),
    cancel() {
      recorder.ondataavailable = null
      if (recorder.state !== 'inactive') recorder.stop()
    },
  }
}
